import administrativeInfoService from '../../services/administrativeInfo.service';
import { getLatestClinicalIntakeContext } from '../../services/clinicalIntake.service';
import { fetchProtocolContextForICF } from '../../utils/protocolContextFetcher';
import { ICF_TITLE_PAGE_PROTOCOL_SOURCES } from '../constants/icfTitlePageData';

const formatAdminInfoForTitlePage = (adminInfo = {}) => {
  if (!adminInfo || typeof adminInfo !== 'object') return '';

  const lines = [];
  const add = (label, value) => {
    const trimmed = value != null ? String(value).trim() : '';
    if (trimmed) lines.push(`${label}: ${trimmed}`);
  };

  add('Official Protocol Title', adminInfo.official_title);
  add('Short Title', adminInfo.short_title);
  add('Trial Acronym', adminInfo.trial_acronym);
  add('Protocol Number', adminInfo.sponsor_protocol_identifier);
  add('Version Number', adminInfo.version_number);
  add(
    'Version Date',
    adminInfo.version_date ? new Date(adminInfo.version_date).toLocaleDateString() : ''
  );
  add('Amendment Identifier', adminInfo.amendment_identifier);
  add('Sponsor Name', adminInfo.sponsor_name);
  add('Co-Sponsor Name', adminInfo.co_sponsor_name);
  add('Local Sponsor Name', adminInfo.local_sponsor_name);
  add(
    'Collaborating Organization(s)',
    Array.isArray(adminInfo.collaborators)
      ? adminInfo.collaborators.filter(Boolean).join('; ')
      : ''
  );
  add('Investigational Product', adminInfo.investigational_product_names);
  add('NCT Number', adminInfo.nct_number);
  add('EudraCT / EU CT Number', adminInfo.eu_ct_number);
  add('IND Number', adminInfo.fda_ind_number);
  add('Other Regulatory Identifier', adminInfo.other_regulatory_identifier);
  add('JRCT Number', adminInfo.jrct_number);
  add('WHO UTN Number', adminInfo.who_utn_number);

  if (Array.isArray(adminInfo.regulatory_identifier_entries)) {
    adminInfo.regulatory_identifier_entries.forEach((entry) => {
      if (entry?.type && entry?.value) {
        add(entry.type, entry.value);
      }
    });
  }

  return lines.length ? `Administrative Information:\n${lines.join('\n')}` : '';
};

/**
 * Aggregates protocol, administrative, and clinical intake context for ICF title page generation.
 * Sources match the prompt: Clinical Protocol, Protocol Synopsis, Sponsor metadata, and intake data.
 */
export const fetchICFTitlePageContext = async (projectId) => {
  const parts = [];

  try {
    const protocolContext = await fetchProtocolContextForICF(
      projectId,
      ICF_TITLE_PAGE_PROTOCOL_SOURCES
    );
    if (protocolContext?.trim()) {
      parts.push(`Clinical Protocol / Synopsis:\n${protocolContext.trim()}`);
    }
  } catch (err) {
    console.warn('Error fetching protocol context for ICF title page:', err);
  }

  try {
    const adminResponse = await administrativeInfoService.getLatestAdministrativeInfo(projectId);
    const adminInfo = adminResponse?.data || adminResponse;
    const adminBlock = formatAdminInfoForTitlePage(adminInfo);
    if (adminBlock) parts.push(adminBlock);
  } catch (err) {
    try {
      const adminResponse = await administrativeInfoService.getAllAdministrativeInfo(projectId);
      const adminInfo = adminResponse?.data?.[0];
      const adminBlock = formatAdminInfoForTitlePage(adminInfo);
      if (adminBlock) parts.push(adminBlock);
    } catch (fallbackErr) {
      console.warn('Error fetching administrative info for ICF title page:', fallbackErr);
    }
  }

  try {
    const intakeContext = await getLatestClinicalIntakeContext(projectId);
    if (intakeContext?.trim()) {
      parts.push(`Clinical Intake:\n${intakeContext.trim()}`);
    }
  } catch (err) {
    console.warn('Error fetching clinical intake for ICF title page:', err);
  }

  return parts.join('\n\n');
};
